import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getDashboardPathFromToken } from "../../utils/dashboardRouting";
import { filterRowsForCurrentCenter } from "../../utils/centerScope";
import { clearContextStorage, getStoredContext, saveContext } from "../../utils/contextStorage";
import { getAssignedCenterId, getStoredUser, shouldScopeToAssignedCenter } from "../../utils/userSession";

const BASE_URL = "https://mis.empowerplus.in";
const ALL_VALUE = "__ALL__";
const FY_OPTIONS = [
    { value: "23-24", label: "2023-24" },
    { value: "24-25", label: "2024-25" },
    { value: "25-26", label: "2025-26" },
    { value: "26-27", label: "2026-27" }
];

const parseDate = (value) => {
    if (!value) return null;
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const getFinancialYearRange = (fy) => {
    const [startYear, endYear] = String(fy || "").split("-");

    if (!startYear || !endYear) {
        return null;
    }

    return {
        fyStart: new Date(Number(`20${startYear}`), 3, 1),
        fyEnd: new Date(Number(`20${endYear}`), 2, 31, 23, 59, 59, 999)
    };
};

const matchesFinancialYear = (project, fy) => {
    if (!fy) return true;

    const range = getFinancialYearRange(fy);

    if (!range) {
        return true;
    }

    const projectStart = parseDate(project?.projectStartDate) || parseDate(project?.agreementDate) || parseDate(project?.createdDate);
    const projectEnd = parseDate(project?.projectEndDate) || projectStart;

    if (projectStart && projectEnd) {
        return projectStart <= range.fyEnd && projectEnd >= range.fyStart;
    }

    return true;
};

const matchesDepartment = (project, department) => {
    if (!department) return true;

    return String(project?.department || project?.departmentCode || "")
        .trim()
        .toUpperCase() === String(department).trim().toUpperCase();
};

export default function ChooseContext() {

    const token = localStorage.getItem("token");
    const [contextLoaded, setContextLoaded] = useState(false);
    const navigate = useNavigate();
    const user = getStoredUser();
    const assignedCenterId = getAssignedCenterId();
    const restrictToCenter = shouldScopeToAssignedCenter();
    const [centerBatches, setCenterBatches] = useState([]);
    const navigateToDashboard = useCallback(() => {
        if (!token) {
            navigate("/login");
            return;
        }

        try {
            navigate(getDashboardPathFromToken(token));
        } catch {
            localStorage.removeItem("token");
            navigate("/login");
        }
    }, [navigate, token]);

    /* ================= COMMON ================= */

    const [msg, setMsg] = useState({ type: "", text: "" });

    const flash = (type, text) => {
        setMsg({ type, text });
        setTimeout(() => setMsg({ type: "", text: "" }), 3000);
    };

    const authHeaders = useMemo(() => ({
        Authorization: `Bearer ${token}`
    }), [token]);

    const safeJson = async (res) => {
        const text = await res.text();
        try {
            return text ? JSON.parse(text) : [];
        } catch {
            return [];
        }
    };

    /* ================= CONTEXT STATE ================= */

    const [deptList, setDeptList] = useState([]);
    const [allProjects, setAllProjects] = useState([]);
    const [projectList, setProjectList] = useState([]);

    const [fy, setFy] = useState(ALL_VALUE);
    const [department, setDepartment] = useState(ALL_VALUE);
    const [project, setProject] = useState(ALL_VALUE);

    const normalizeProjectKey = useCallback((value) => {
        const normalized = String(value || "")
            .trim()
            .toLowerCase()
            .replace(/(\d{4})-(\d{4})/g, (_, start, end) => `${start}-${end.slice(-2)}`);

        if (!normalized) {
            return [];
        }

        const parts = normalized.split("/").filter(Boolean);
        const variants = new Set([normalized]);

        if (parts.length) {
            variants.add(parts[parts.length - 1]);
        }

        return [...variants];
    }, []);

    const allowedProjectIds = useMemo(() => {
        return new Set(
            centerBatches
                .flatMap((batch) => normalizeProjectKey(batch?.project?.projectId || batch?.projectId || batch?.projectName || ""))
        );
    }, [centerBatches, normalizeProjectKey]);



    /* ================= FETCH PROJECTS ================= */

    const fetchProjects = useCallback(async () => {
        try {
            const res = await fetch(`${BASE_URL}/api/projects`, { headers: authHeaders });
            const data = await safeJson(res);
            setAllProjects(Array.isArray(data) ? data : data?.content || []);
        } catch {
            console.error("Project load failed");
            setAllProjects([]);
        }
    }, [authHeaders]);

    const fetchCenterBatches = useCallback(async () => {
        if (!restrictToCenter || !assignedCenterId) {
            setCenterBatches([]);
            return;
        }

        try {
            const res = await fetch(`${BASE_URL}/api/batches`, {
                headers: authHeaders
            });
            const data = await safeJson(res);
            const rows = Array.isArray(data) ? data : data?.content || [];
            setCenterBatches(filterRowsForCurrentCenter(rows));
        } catch {
            setCenterBatches([]);
        }
    }, [assignedCenterId, authHeaders, restrictToCenter]);



    /* ================= EFFECTS ================= */
    useEffect(() => {

        const ctx = getStoredContext();

        setFy(ctx.fy || ALL_VALUE);
        setDepartment(ctx.department || ALL_VALUE);
        setProject(ctx.project || ALL_VALUE);
        setContextLoaded(Boolean(ctx.centerId || ctx.fy || ctx.department || ctx.project));

    }, [assignedCenterId]);

    useEffect(() => {
        fetchProjects();
        fetchCenterBatches();
    }, [fetchCenterBatches, fetchProjects]);

    useEffect(() => {
        const nextDepartments = allProjects
            .filter((item) => matchesFinancialYear(item, fy === ALL_VALUE ? "" : fy))
            .reduce((acc, item) => {
                const departmentCode = String(item?.department || "").trim();

                if (!departmentCode || acc.some((entry) => entry.departmentCode === departmentCode)) {
                    return acc;
                }

                acc.push({
                    id: departmentCode,
                    departmentCode,
                    departmentName: departmentCode
                });

                return acc;
            }, []);

        setDeptList(nextDepartments);
    }, [allProjects, fy]);

    useEffect(() => {
        const nextProjects = allProjects.filter((item) => (
            matchesFinancialYear(item, fy === ALL_VALUE ? "" : fy) &&
            matchesDepartment(item, department === ALL_VALUE ? "" : department)
        ));

        const scopedProjects = restrictToCenter && allowedProjectIds.size
            ? nextProjects.filter((item) =>
                normalizeProjectKey(item.projectId || item.projectName).some((key) => allowedProjectIds.has(key))
            )
            : nextProjects;

        setProjectList(scopedProjects.length ? scopedProjects : nextProjects);
    }, [allProjects, allowedProjectIds, department, fy, normalizeProjectKey, restrictToCenter]);

    useEffect(() => {
        if (project !== ALL_VALUE && projectList.length && !projectList.some((item) => item.projectId === project)) {
            setProject(ALL_VALUE);
        }
    }, [project, projectList]);

    /* ================= CONFIRM ================= */

    const handleConfirm = () => {
        const normalizedFy = fy === ALL_VALUE ? "" : fy;
        const normalizedDepartment = department === ALL_VALUE ? "" : department;
        const normalizedProject = project === ALL_VALUE ? "" : project;
        const selectedProject = projectList.find((item) => item.projectId === normalizedProject);

        saveContext({
            fy: normalizedFy,
            department: normalizedDepartment,
            project: normalizedProject,
            projectName: selectedProject?.projectName || "",
            centerId: restrictToCenter ? assignedCenterId || "" : ""
        });

        setContextLoaded(true);

        flash("success", "Context selected successfully");

        navigateToDashboard();

    };

    const handleReset = () => {

        clearContextStorage();

        setFy(ALL_VALUE);
        setDepartment(ALL_VALUE);
        setProject(ALL_VALUE);

        setContextLoaded(false);

        flash("success", "Context reset successfully. Showing all center data.");

    };



    /* ================= UI ================= */

    return (

        <>
            <style>{`
.context-page{
height:100vh;
display:flex;
justify-content:center;
align-items:center;
// background:linear-gradient(135deg,#1f2937,#111827);
font-family:Segoe UI;
}

.context-card{
background:white;
padding:50px;
border-radius:14px;
width:760px;
box-shadow:0 25px 60px rgba(0,0,0,0.2);
text-align:center;
}

.context-header h2{
margin-bottom:10px;
color:#111827;
}

.context-header p{
color:#6b7280;
}

.context-row{
display:flex;
flex-wrap:wrap;
gap:25px;
margin-top:35px;
}

.context-field{
flex:1;
min-width:180px;
text-align:left;
}

.context-field label{
display:block;
margin-bottom:8px;
font-weight:600;
}

.context-field select{
width:100%;
padding:12px;
border-radius:8px;
border:1px solid #d1d5db;
outline:none;
font-size:14px;
}

.context-field select:focus{
border-color:#4f46e5;
box-shadow:0 0 0 3px rgba(79,70,229,0.2);
}

.context-button{
margin-top:35px;
}

.context-button button{
background:#4f46e5;
color:white;
border:none;
padding:12px 40px;
font-size:15px;
border-radius:8px;
cursor:pointer;
}

.context-button button:hover{
background:#4338ca;
}

.msg{
margin-top:15px;
font-size:14px;
}

.msg.success{
color:green;
}

.msg.error{
color:red;
}
`}</style>

            <div className="context-page">

                <div className="context-card">

                    <div className="context-header">
                        <h2>Welcome, Center Manager</h2>
                        <p>Select a project context or keep All Data to view your full center.</p>
                        {restrictToCenter && assignedCenterId ? (
                            <p style={{ marginTop: "8px", fontWeight: 700, color: "#166534" }}>
                                {`Assigned Center: ${assignedCenterId}${user?.centerName ? ` (${user.centerName})` : ""}`}
                            </p>
                        ) : null}
                    </div>

                    <div className="context-row">

                        {/* FY */}
                        <div className="context-field">
                            <label>Financial Year</label>

                            <select
                                value={fy}
                                onChange={(e) => {
                                    setFy(e.target.value);
                                    setDepartment(ALL_VALUE);
                                    setProject(ALL_VALUE);
                                    setDeptList([]);
                                    setProjectList([]);
                                }}
                            >
                                <option value={ALL_VALUE}>All Data</option>
                                {FY_OPTIONS.map((item) => (
                                    <option key={item.value} value={item.value}>
                                        {item.label}
                                    </option>
                                ))}
                            </select>
                        </div>


                        {/* Department */}
                        <div className="context-field">
                            <label>Department</label>

                            <select
                                value={department}
                                onChange={(e) => {
                                    setDepartment(e.target.value);
                                    setProject(ALL_VALUE);
                                    setProjectList([]);
                                }}
                            >

                                <option value={ALL_VALUE}>All Data</option>

                                {deptList.map((d) => (
                                    <option key={d.id} value={d.departmentCode}>
                                        {d.departmentName}
                                    </option>
                                ))}

                            </select>

                        </div>



                        {/* Project */}
                        <div className="context-field">
                            <label>Project</label>

                            <select
                                value={project}
                                onChange={(e) => {
                                    setProject(e.target.value);
                                }}
                            >

                                <option value={ALL_VALUE}>All Data</option>

                                {projectList.map((p) => (
                                    <option key={p.projectId} value={p.projectId}>
                                        {p.projectName}
                                    </option>
                                ))}

                            </select>

                            {restrictToCenter && department !== ALL_VALUE && !projectList.length ? (
                                <small style={{ color: "#b45309" }}>
                                    No projects found for your assigned center in this FY and department.
                                </small>
                            ) : null}

                        </div>

                    </div>



                    <div className="context-button">

                        {!contextLoaded && (
                            <button onClick={handleConfirm}>
                                Confirm
                            </button>
                        )}

                        {contextLoaded && (
                            <>
                                <button onClick={handleConfirm}>
                                    Update Context
                                </button>
                                <button
                                    style={{ background: "#dc2626", marginLeft: "10px" }}
                                    onClick={handleReset}
                                >
                                    Reset Context
                                </button>
                            </>
                        )}

                    </div>

                    {msg.text && (
                        <div className={`msg ${msg.type}`}>
                            {msg.text}
                        </div>
                    )}

                </div>

            </div>

        </>

    );

}




// import React, { useEffect, useMemo, useState } from "react";
// import { createPortal } from "react-dom";

// const BASE_URL = "https://mis.empowerplus.in";

// export default function CenterManagerDashboard() {

//     const token = localStorage.getItem("token");

//     /* ================= COMMON ================= */

//     const [msg, setMsg] = useState({ type: "", text: "" });

//     const flash = (type, text) => {
//         setMsg({ type, text });
//         setTimeout(() => setMsg({ type: "", text: "" }), 3000);
//     };

//     const authHeaders = useMemo(() => ({
//         Authorization: `Bearer ${token}`
//     }), [token]);

//     const safeJson = async (res) => {
//         const t = await res.text();
//         try {
//             return t ? JSON.parse(t) : [];
//         } catch {
//             return [];
//         }
//     };



//     /* ================= CONTEXT STATE ================= */

//     // const [fyList, setFyList] = useState([]);
//     const [deptList, setDeptList] = useState([]);
//     const [projectList, setProjectList] = useState([]);

//     const [fy, setFy] = useState("");
//     const [department, setDepartment] = useState("");
//     const [project, setProject] = useState("");



//     /* ================= FETCH FY ================= */

//     // const fetchFinancialYears = async () => {

//     //     try {

//     //         const res = await fetch(`${BASE_URL}/api/financial-years`, {
//     //             headers: authHeaders
//     //         });

//     //         const data = await safeJson(res);
//     //         setFyList(data);

//     //     } catch {
//     //         setFyList([]);
//     //     }

//     // };



//     /* ================= FETCH DEPARTMENTS ================= */

//     const fetchDepartments = async (fy) => {

//         try {

//             const res = await fetch(
//                 `${BASE_URL}/departments?fy=${fy}`,
//                 { headers: authHeaders }
//             );

//             const data = await safeJson(res);
//             setDeptList(data);

//         } catch {
//             setDeptList([]);
//         }

//     };



//     /* ================= FETCH PROJECTS ================= */

//     const fetchProjects = async (department) => {

//         try {

//             const res = await fetch(
//                 `${BASE_URL}/projects?department=${department}`,
//                 { headers: authHeaders }
//             );

//             const data = await safeJson(res);
//             setProjectList(data);

//         } catch {
//             setProjectList([]);
//         }

//     };



//     /* ================= EFFECTS ================= */

//     useEffect(() => {

//         // fetchFinancialYears();

//         const saved = localStorage.getItem("context");

//         if (saved) {

//             const ctx = JSON.parse(saved);

//             setFy(ctx.fy);
//             setDepartment(ctx.department);
//             setProject(ctx.project);

//         }

//     }, []);



//     useEffect(() => {

//         if (fy) {

//             setDepartment("");
//             setProject("");
//             fetchDepartments(fy);

//         }

//     }, [fy]);



//     useEffect(() => {

//         if (department) {

//             setProject("");
//             fetchProjects(department);

//         }

//     }, [department]);



//     /* ================= CONFIRM ================= */

//     const handleConfirm = () => {

//         if (!fy || !department || !project) {

//             flash("error", "Please select FY, Department and Project");
//             return;

//         }

//         const context = { fy, department, project };

//         localStorage.setItem("context", JSON.stringify(context));

//         flash("success", "Context selected successfully");

//         console.log(context);

//     };



//     /* ================= UI ================= */

//     return (

//         <>
//             <style>{`

// .context-page{
// height:100vh;
// display:flex;
// justify-content:center;
// align-items:center;
// background:linear-gradient(135deg,#1f2937,#111827);
// font-family:Segoe UI;
// }

// .context-card{
// background:white;
// padding:50px;
// border-radius:14px;
// width:850px;
// box-shadow:0 25px 60px rgba(0,0,0,0.2);
// text-align:center;
// }

// .context-header h2{
// margin-bottom:10px;
// color:#111827;
// }

// .context-header p{
// color:#6b7280;
// }

// .context-row{
// display:flex;
// gap:25px;
// margin-top:35px;
// }

// .context-field{
// flex:1;
// text-align:left;
// }

// .context-field label{
// display:block;
// margin-bottom:8px;
// font-weight:600;
// }

// .context-field select{
// width:100%;
// padding:12px;
// border-radius:8px;
// border:1px solid #d1d5db;
// outline:none;
// font-size:14px;
// }

// .context-field select:focus{
// border-color:#4f46e5;
// box-shadow:0 0 0 3px rgba(79,70,229,0.2);
// }

// .context-button{
// margin-top:35px;
// }

// .context-button button{
// background:#4f46e5;
// color:white;
// border:none;
// padding:12px 40px;
// font-size:15px;
// border-radius:8px;
// cursor:pointer;
// }

// .context-button button:hover{
// background:#4338ca;
// }

// .msg{
// margin-top:15px;
// font-size:14px;
// }

// .msg.success{
// color:green;
// }

// .msg.error{
// color:red;
// }

// `}</style>

//             <div className="context-page">

//                 <div className="context-card">

//                     <div className="context-header">
//                         <h2>Welcome, Center Manager</h2>
//                         <p>Please Select Your Working Context</p>
//                     </div>

//                     <div className="context-row">

//                         <div className="context-field">
//                             <label>Financial Year</label>

//                             <select
//                                 value={fy}
//                                 onChange={(e) => setFy(e.target.value)}
//                             >

//                                 <option value="">Select FY</option>
//                                 <option value="23-24">2023-24</option>
//                                 <option value="24-25">2024-25</option>
//                                 <option value="25-26">2025-26</option>
//                                 <option value="26-27">2026-27</option>

//                             </select>
//                         </div>


//                         <div className="context-field">
//                             <label>Department</label>
//                             <select
//                                 value={department}
//                                 onChange={(e) => setDepartment(e.target.value)}
//                                 disabled={!fy}
//                             >
//                                 <option value="">Select Department</option>

//                                 {deptList.map((d) => (
//                                     <option key={d.id} value={d.id}>
//                                         {d.departmentName}
//                                     </option>
//                                 ))}

//                             </select>
//                         </div>



//                         <div className="context-field">
//                             <label>Project</label>
//                             <select
//                                 value={project}
//                                 onChange={(e) => setProject(e.target.value)}
//                                 disabled={!department}
//                             >
//                                 <option value="">Select Project</option>

//                                 {projectList.map((p) => (
//                                     <option key={p.projectId} value={p.projectId}>
//                                         {p.projectName}
//                                     </option>
//                                 ))}

//                             </select>
//                         </div>

//                     </div>



//                     <div className="context-button">
//                         <button onClick={handleConfirm}>
//                             Confirm
//                         </button>
//                     </div>

//                     {msg.text && (
//                         <div className={`msg ${msg.type}`}>
//                             {msg.text}
//                         </div>
//                     )}

//                 </div>

//             </div>

//         </>

//     );

// }
